import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { derivePreviewText } from "@/lib/text";
import type { ExploreEventItem, ExploreEventFilters, EventDateFilter, EventPriceFilter } from "@/types/exploreEvent";
import type { EventType } from "@/types/events";
import { startOfDay, endOfDay, startOfWeek, endOfWeek, addDays, isWithinInterval, parseISO } from "date-fns";
import { isActivityPubliclyVisible } from "@/lib/events/publicVisibility";
import { fetchCourseOccurrenceInfo, resolveCourseVisibilityRow } from "@/lib/events/resolveCourseOccurrences";

const DEFAULT_FILTERS: ExploreEventFilters = {
  dateFilter: 'upcoming',
  eventTypes: [],
  priceFilter: 'all',
  regions: [],
  cities: [],
};

export interface UseExploreEventsResult {
  events: ExploreEventItem[];
  loading: boolean;
  error?: string;
  filters: ExploreEventFilters;
  updateFilters: (updates: Partial<ExploreEventFilters>) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  // Facets for filter UI
  facets: {
    eventTypes: { value: EventType; count: number }[];
    regions: { value: string; count: number }[];
    cities: { value: string; count: number }[];
  };
}

/**
 * Hook for fetching and filtering public events for Explore page.
 */
export function useExploreEvents(): UseExploreEventsResult {
  const [allEvents, setAllEvents] = useState<ExploreEventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [filters, setFilters] = useState<ExploreEventFilters>(DEFAULT_FILTERS);

  // Fetch all upcoming public events
  useEffect(() => {
    let cancelled = false;

    async function fetchEvents() {
      setLoading(true);
      setError(undefined);

      try {
        // Fetch public events — do NOT filter by start_date here:
        // timing_mode='anytime'/'season' have placeholder dates and must not be excluded.
        const { data, error: fetchError } = await supabase
          .rpc("get_public_events_with_bookings" as any, {
            p_limit: 100,
          });

        if (import.meta.env.DEV) {
          console.log('[ExploreEvents] query returned', data?.length ?? 0, 'rows');
        }

        if (fetchError) {
          console.error("Error fetching events:", fetchError);
          if (!cancelled) {
            setError("Kunde inte hämta event.");
            setAllEvents([]);
          }
          return;
        }

        const publicRows = ((data || []) as any[]).filter(
          (row) => row.event_type === 'event' || row.event_type === 'course',
        );

        if (publicRows.length === 0) {
          if (!cancelled) {
            setAllEvents([]);
          }
          return;
        }

        // Fetch producer info + farm sale flags in parallel
        const eventIds = publicRows.map(e => e.id);
        const producerIds = [...new Set(publicRows.map(e => e.producer_id).filter(Boolean))] as string[];
        
        const [{ data: producers }, { data: farmSaleRows }] = await Promise.all([
          supabase
            .from("producers")
            .select("id, name, slug, city, region, county, lat, lng")
            .in("id", producerIds)
            .eq("status", "public"),
          supabase
            .from("producer_events")
            .select("id, includes_farm_sale, includes_farm_sale_alcohol")
            .in("id", eventIds),
        ]);

        const producerMap = new Map(producers?.map(p => [p.id, p]) || []);
        const farmSaleMap = new Map(
          (farmSaleRows || []).map((r: any) => [r.id, { sale: !!r.includes_farm_sale, alcohol: !!r.includes_farm_sale_alcohol }])
        );

        // Resolve course occurrences: override start_date with next upcoming
        // occurrence and exclude courses with no future occurrences.
        const courseIds = publicRows
          .filter(e => e.event_type === 'course')
          .map(e => e.id);
        const courseInfo = await fetchCourseOccurrenceInfo(courseIds);

        const resolvedData = publicRows
          .map((event) => resolveCourseVisibilityRow(event, courseInfo.get(event.id)))
          .filter((event): event is NonNullable<typeof event> => event !== null);

        // Map to ExploreEventItem — filter out past date_time events
        const now = new Date();
        const events: ExploreEventItem[] = resolvedData
          .filter(event => {
            const producer = producerMap.get(event.producer_id!);
            if (!producer) return false; // Only include events with visible producers
            // Canonical public visibility filter
            return isActivityPubliclyVisible({
              timingMode: (event.timing_mode as string) || 'date_time',
              startDate: event.start_date,
              endDate: event.end_date,
            }, now);
          })
          .map(event => {
            const producer = producerMap.get(event.producer_id!)!;
            const useCustomLocation = !event.use_producer_address && event.custom_lat != null;
            
            return {
              id: event.id,
              title: event.title,
              eventType: event.event_type as EventType,
              shortDescription: derivePreviewText(event.description, 160),
              coverImageUrl: null, // Media from media_assets via useBatchCovers
              startDate: event.start_date,
              endDate: event.end_date,
              priceOre: event.price_ore || 0,
              isFree: event.is_free || false,
              maxSpots: event.max_spots || 10,
              bookedSpots: event.booked_spots || 0,
              lat: useCustomLocation ? event.custom_lat : producer.lat,
              lng: useCustomLocation ? event.custom_lng : producer.lng,
              address: useCustomLocation ? event.custom_address : null,
              producerId: event.producer_id,
              producerName: producer.name,
              producerSlug: producer.slug,
              producerCity: producer.city,
              producerCoverImageUrl: null, // No legacy image fields
              timingMode: (event.timing_mode as 'date_time' | 'open' | 'anytime' | 'season') || 'date_time',
              outingTags: event.outing_tags || [],
              useProducerAddress: event.use_producer_address ?? true,
              customAddress: event.custom_address ?? null,
              includesFarmSale: farmSaleMap.get(event.id)?.sale ?? false,
              includesFarmSaleAlcohol: farmSaleMap.get(event.id)?.alcohol ?? false,
              occurrencesCount: event.occurrences_count ?? 0,
              href: `/event/${event.id}`,
            };
          });

        if (!cancelled) {
          setAllEvents(events);
        }
      } catch (err) {
        console.error("Exception fetching events:", err);
        if (!cancelled) {
          setError("Ett oväntat fel uppstod.");
          setAllEvents([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchEvents();

    return () => {
      cancelled = true;
    };
  }, []);

  // Filter events based on current filters
  const filteredEvents = useMemo(() => {
    return allEvents.filter(event => {
      // Text search
      if (filters.q) {
        const query = filters.q.toLowerCase();
        const matches = 
          event.title.toLowerCase().includes(query) ||
          event.producerName.toLowerCase().includes(query) ||
          (event.producerCity?.toLowerCase().includes(query) ?? false) ||
          (event.shortDescription?.toLowerCase().includes(query) ?? false);
        if (!matches) return false;
      }

      // Date filter — open/anytime/season events always pass date filters
      if (event.timingMode === 'open' || event.timingMode === 'anytime' || event.timingMode === 'season') {
        // Always visible regardless of date filter
      } else if (filters.dateFilter !== 'all' && filters.dateFilter !== 'upcoming') {
        const eventDate = parseISO(event.startDate);
        const today = new Date();
        
        switch (filters.dateFilter) {
          case 'today': {
            const todayStart = startOfDay(today);
            const todayEnd = endOfDay(today);
            if (!isWithinInterval(eventDate, { start: todayStart, end: todayEnd })) {
              return false;
            }
            break;
          }
          case 'weekend': {
            const nextSaturday = startOfDay(addDays(startOfWeek(today, { weekStartsOn: 1 }), 5));
            const nextSunday = endOfDay(addDays(nextSaturday, 1));
            if (!isWithinInterval(eventDate, { start: nextSaturday, end: nextSunday })) {
              return false;
            }
            break;
          }
          case 'custom': {
            if (filters.customDateFrom) {
              const from = startOfDay(parseISO(filters.customDateFrom));
              if (eventDate < from) return false;
            }
            if (filters.customDateTo) {
              const to = endOfDay(parseISO(filters.customDateTo));
              if (eventDate > to) return false;
            }
            break;
          }
        }
      }

      // Event type filter
      if (filters.eventTypes.length > 0) {
        if (!filters.eventTypes.includes(event.eventType)) {
          return false;
        }
      }

      // Price filter
      if (filters.priceFilter === 'free' && !event.isFree) {
        return false;
      }
      if (filters.priceFilter === 'paid' && event.isFree) {
        return false;
      }

      return true;
    });
  }, [allEvents, filters]);

  // Calculate facets from all events (not filtered)
  const facets = useMemo(() => {
    const typeCount = new Map<EventType, number>();
    const regionCount = new Map<string, number>();
    const cityCount = new Map<string, number>();

    allEvents.forEach(event => {
      // Event types
      typeCount.set(event.eventType, (typeCount.get(event.eventType) || 0) + 1);
      
      // Cities
      if (event.producerCity) {
        cityCount.set(event.producerCity, (cityCount.get(event.producerCity) || 0) + 1);
      }
    });

    return {
      eventTypes: Array.from(typeCount.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count),
      regions: Array.from(regionCount.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count),
      cities: Array.from(cityCount.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count),
    };
  }, [allEvents]);

  const updateFilters = useCallback((updates: Partial<ExploreEventFilters>) => {
    setFilters(prev => ({ ...prev, ...updates }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      (filters.q?.length ?? 0) > 0 ||
      filters.dateFilter !== 'upcoming' ||
      filters.eventTypes.length > 0 ||
      filters.priceFilter !== 'all' ||
      filters.regions.length > 0 ||
      filters.cities.length > 0
    );
  }, [filters]);

  return {
    events: filteredEvents,
    loading,
    error,
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    facets,
  };
}
